import React, {useState} from 'react'
import {Row} from 'jsxstyle'
import {
  Bold,
  Italic,
  Underline,
  Strikethrough,
  Quote,
  List,
  ListOrdered,
  Heading1,
  Heading2,
  Heading3,
  AlignLeft,
  AlignCenter,
  AlignRight,
} from 'lucide-react'
import {RichInputRef} from '../RichInput'
import ToolbarButton from './ToolbarButton'
import ColorPicker from './ColorPicker'

interface RichTextToolbarProps {
  richRef: React.RefObject<RichInputRef>
  exec: (fn: (editor: RichInputRef['editor']) => void) => void
}

const RichTextToolbar: React.FC<RichTextToolbarProps> = ({richRef, exec}) => {
  const [hoveredTooltip, setHoveredTooltip] = useState<string | null>(null)

  const handleTooltipHover = (tooltipName: string) => {
    setHoveredTooltip(tooltipName)
  }

  const handleTooltipLeave = () => {
    setHoveredTooltip(null)
  }

  return (
    <Row padding="8px" marginBottom="8px" gap="4px" alignItems="center" flexWrap="wrap" backgroundColor="#f8f9fa" borderRadius="6px">
      <ToolbarButton
        tooltipName="bold"
        isActive={richRef.current?.editor?.isActive('bold')}
        onClick={() => exec((editor) => editor?.chain().toggleBold().run())}
        onMouseEnter={handleTooltipHover}
        onMouseLeave={handleTooltipLeave}
        hoveredTooltip={hoveredTooltip}
        icon={<Bold size={16} />}
        tooltipText="Bold"
      />

      <ToolbarButton
        tooltipName="italic"
        isActive={richRef.current?.editor?.isActive('italic')}
        onClick={() => exec((editor) => editor?.chain().toggleItalic().run())}
        onMouseEnter={handleTooltipHover}
        onMouseLeave={handleTooltipLeave}
        hoveredTooltip={hoveredTooltip}
        icon={<Italic size={16} />}
        tooltipText="Italic"
      />

      <ToolbarButton
        tooltipName="underline"
        isActive={richRef.current?.editor?.isActive('underline')}
        onClick={() => exec((editor) => editor?.chain().toggleUnderline().run())}
        onMouseEnter={handleTooltipHover}
        onMouseLeave={handleTooltipLeave}
        hoveredTooltip={hoveredTooltip}
        icon={<Underline size={16} />}
        tooltipText="Underline"
      />

      <ToolbarButton
        tooltipName="strike"
        isActive={richRef.current?.editor?.isActive('strike')}
        onClick={() => exec((editor) => editor?.chain().toggleStrike().run())}
        onMouseEnter={handleTooltipHover}
        onMouseLeave={handleTooltipLeave}
        hoveredTooltip={hoveredTooltip}
        icon={<Strikethrough size={16} />}
        tooltipText="Strikethrough"
      />

      <ToolbarButton
        tooltipName="blockquote"
        isActive={richRef.current?.editor?.isActive('blockquote')}
        onClick={() => exec((editor) => editor?.chain().toggleBlockquote().run())}
        onMouseEnter={handleTooltipHover}
        onMouseLeave={handleTooltipLeave}
        hoveredTooltip={hoveredTooltip}
        icon={<Quote size={16} />}
        tooltipText="Blockquote"
      />

      <ToolbarButton
        tooltipName="bulletList"
        isActive={richRef.current?.editor?.isActive('bulletList')}
        onClick={() => exec((editor) => editor?.chain().toggleBulletList().run())}
        onMouseEnter={handleTooltipHover}
        onMouseLeave={handleTooltipLeave}
        hoveredTooltip={hoveredTooltip}
        icon={<List size={16} />}
        tooltipText="Bullet List"
      />

      <ToolbarButton
        tooltipName="orderedList"
        isActive={richRef.current?.editor?.isActive('orderedList')}
        onClick={() => exec((editor) => editor?.chain().toggleOrderedList().run())}
        onMouseEnter={handleTooltipHover}
        onMouseLeave={handleTooltipLeave}
        hoveredTooltip={hoveredTooltip}
        icon={<ListOrdered size={16} />}
        tooltipText="Numbered List"
      />

      <ToolbarButton
        tooltipName="h1"
        isActive={richRef.current?.editor?.isActive('heading', {level: 1})}
        onClick={() => exec((editor) => editor?.chain().toggleHeading({level: 1}).run())}
        onMouseEnter={handleTooltipHover}
        onMouseLeave={handleTooltipLeave}
        hoveredTooltip={hoveredTooltip}
        icon={<Heading1 size={16} />}
        tooltipText="Heading 1"
      />

      <ToolbarButton
        tooltipName="h2"
        isActive={richRef.current?.editor?.isActive('heading', {level: 2})}
        onClick={() => exec((editor) => editor?.chain().toggleHeading({level: 2}).run())}
        onMouseEnter={handleTooltipHover}
        onMouseLeave={handleTooltipLeave}
        hoveredTooltip={hoveredTooltip}
        icon={<Heading2 size={16} />}
        tooltipText="Heading 2"
      />

      <ToolbarButton
        tooltipName="h3"
        isActive={richRef.current?.editor?.isActive('heading', {level: 3})}
        onClick={() => exec((editor) => editor?.chain().toggleHeading({level: 3}).run())}
        onMouseEnter={handleTooltipHover}
        onMouseLeave={handleTooltipLeave}
        hoveredTooltip={hoveredTooltip}
        icon={<Heading3 size={16} />}
        tooltipText="Heading 3"
      />

      <ToolbarButton
        tooltipName="alignLeft"
        isActive={richRef.current?.editor?.isActive({textAlign: 'left'})}
        onClick={() => exec((editor) => editor?.chain().setTextAlign('left').run())}
        onMouseEnter={handleTooltipHover}
        onMouseLeave={handleTooltipLeave}
        hoveredTooltip={hoveredTooltip}
        icon={<AlignLeft size={16} />}
        tooltipText="Align Left"
      />

      <ToolbarButton
        tooltipName="alignCenter"
        isActive={richRef.current?.editor?.isActive({textAlign: 'center'})}
        onClick={() => exec((editor) => editor?.chain().setTextAlign('center').run())}
        onMouseEnter={handleTooltipHover}
        onMouseLeave={handleTooltipLeave}
        hoveredTooltip={hoveredTooltip}
        icon={<AlignCenter size={16} />}
        tooltipText="Align Center"
      />

      <ToolbarButton
        tooltipName="alignRight"
        isActive={richRef.current?.editor?.isActive({textAlign: 'right'})}
        onClick={() => exec((editor) => editor?.chain().setTextAlign('right').run())}
        onMouseEnter={handleTooltipHover}
        onMouseLeave={handleTooltipLeave}
        hoveredTooltip={hoveredTooltip}
        icon={<AlignRight size={16} />}
        tooltipText="Align Right"
      />

      <ColorPicker
        onColorChange={(color) => exec((editor) => editor?.chain().setColor(color).run())}
        onMouseEnter={handleTooltipHover}
        onMouseLeave={handleTooltipLeave}
        hoveredTooltip={hoveredTooltip}
      />
    </Row>
  )
}

export default RichTextToolbar
